import React, { useState, useCallback, useEffect, useRef } from "react";
import styled from "styled-components";
import InfoIcon from "@mui/icons-material/Info";
import IyugaTooltip from "../../../IyugaTooltip";
import { timerService } from "../services/TimerService";

const TimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const TimeValue = styled.div`
  font-family: "DM Sans";
  font-weight: 700;
  font-size: 24px;
  line-height: 31px;
  text-align: center;
  color: ${(props) =>
    props.unit === "second" ? "rgb(226, 34, 34)" : "#000000"};
`;

const TimeLabel = styled.div`
  font-family: "DM Sans";
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
  color: ${(props) =>
    props.unit === "second" ? "rgb(226, 34, 34)" : "#000000"};
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  cursor: help;
  color: #000000;
`;

const Timer = React.memo(
  ({ expected_deletion_time, onWheelDelete, wheelId, hideTooltip = false }) => {
    const [timeLeft, setTimeLeft] = useState(null);
    const updateCount = useRef(0);
    const mounted = useRef(false);

    // Memoize the delete callback
    const handleDelete = useCallback(() => {
      onWheelDelete?.(wheelId);
    }, [wheelId, onWheelDelete]);

    // Memoize the time update callback
    const handleTimeUpdate = useCallback(
      (newTimeLeft) => {
        updateCount.current++;
        if (mounted.current) {
          setTimeLeft(newTimeLeft);
          if (newTimeLeft?.isExpired) {
            handleDelete();
          }
        }
      },
      [handleDelete]
    );

    console.log("TIMER COMPONENT RERENDERED");

    useEffect(() => {
      mounted.current = true;

      // Get initial time immediately
      const initialTime = timerService.calculateTimeRemaining(
        expected_deletion_time
      );
      if (initialTime) {
        handleTimeUpdate(initialTime);
      }

      // Subscribe to updates
      const unsubscribe = timerService.subscribe(
        expected_deletion_time,
        handleTimeUpdate
      );

      return () => {
        mounted.current = false;
        unsubscribe();
      };
    }, [expected_deletion_time, handleTimeUpdate]);

    // Don't render anything if we don't have valid time
    if (!timeLeft) {
      return null;
    }

    return (
      <>
        {!hideTooltip && (
          <InfoIconWrapper>
            <IyugaTooltip
              placement="right"
              tooltip={`This Wheel is disabled due to account storage limits after downgrading and will be permanently deleted after ${
                timeLeft.value
              } ${
                timeLeft.unit === "day"
                  ? "days"
                  : timeLeft.unit === "hour"
                  ? "hours"
                  : timeLeft.unit === "minute"
                  ? "minutes"
                  : "seconds"
              }.`}
            >
              <InfoIcon fontSize="small" />
            </IyugaTooltip>
          </InfoIconWrapper>
        )}

        <TimeContainer>
          <TimeValue unit={timeLeft.unit}>{timeLeft.value}</TimeValue>
          <TimeLabel unit={timeLeft.unit}>
            {timeLeft.unit === "day"
              ? "Days"
              : timeLeft.unit === "hour"
              ? "Hrs"
              : timeLeft.unit === "minute"
              ? "Mins"
              : "Secs"}{" "}
            Left
          </TimeLabel>
        </TimeContainer>
      </>
    );
  }
);

Timer.displayName = "Timer";

export default Timer;
